import { useMemo, memo } from 'react';
import { useSelector } from 'react-redux';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTranslation } from '@hooks';
import Doughnut from '@components/Charts/Doughnut';
import { formatNumber } from '@helpers/formatNumbers';
import { SYNC_VIEW } from '@constants/profiles';
import { VIEW_COLORS, VIEW_LABEL_KEY } from '@helpers/profileSync';

const ORDER = [
    SYNC_VIEW.DISABLED,
    SYNC_VIEW.NOT_PUBLISHED,
    SYNC_VIEW.CONFLICT,
    SYNC_VIEW.IN_PROGRESS,
    SYNC_VIEW.SYNCED,
];

const createChartData = (counts, hasData, t) => {
    const EMPTY_SPACE = '                                             ';
    const colors = ORDER.map(v => VIEW_COLORS[v]);
    const dataMap = ORDER.map(view => ({
        label: `${t(VIEW_LABEL_KEY[view])} - ${formatNumber(counts[view])} ${EMPTY_SPACE}`,
        value: counts[view],
    }));

    return {
        labels: dataMap.map(d => d.label),
        datasets: [
            {
                label: '',
                data: !hasData ? [1] : dataMap.map(d => d.value),
                backgroundColor: !hasData ? ['#F2F2F2', ...colors] : colors,
                hoverBackgroundColor: !hasData ? ['#F2F2F2', ...colors] : colors,
                borderColor: !hasData ? ['#F2F2F2', ...colors] : colors,
                hoverBorderColor: !hasData ? ['#F2F2F2', ...colors] : colors,
                borderWidth: 1,
            },
        ],
    };
};

const StatusChart = memo(() => {
    const { t } = useTranslation();
    const {
        loading,
        totalCount,
        conflictCount,
        notPublishedCount,
        inProgressCount,
        syncedCount,
        naCount,
        syncedWithConflictsCount,
    } = useSelector(state => state.profiles);
    const isMobile = useMediaQuery('@media (max-width: 600px)');

    const counts = useMemo(
        () => ({
            [SYNC_VIEW.DISABLED]: naCount,
            [SYNC_VIEW.SYNCED]: syncedCount + syncedWithConflictsCount,
            [SYNC_VIEW.CONFLICT]: conflictCount,
            [SYNC_VIEW.NOT_PUBLISHED]: notPublishedCount,
            [SYNC_VIEW.IN_PROGRESS]: inProgressCount,
        }),
        [naCount, syncedCount, syncedWithConflictsCount, conflictCount, notPublishedCount, inProgressCount],
    );

    const hasData = useMemo(() => ORDER.some(v => counts[v] > 0), [counts]);
    const datasets = useMemo(() => createChartData(counts, hasData, t), [counts, hasData]);

    const getTextProps = () => {
        if (totalCount >= 1000) {
            return { fontData: '32px  Rubik, sans-serif', yTitleOffset: 15 };
        } else if (totalCount >= 100) {
            return { fontData: '40px  Rubik, sans-serif', yTitleOffset: 20 };
        }
        return { fontData: '48px  Rubik, sans-serif', yTitleOffset: 20 };
    };

    return (
        <div
            style={{
                position: 'relative',
                height: '130px',
                width: '130px',
                marginBottom: isMobile ? '16px' : 0,
            }}
        >
            {/* Hide only on the very first load (before any response). Once we
                have a totalCount, keep the donut visible during refetches so it
                doesn't blank-flash when the table skeleton kicks in. */}
            {!loading || totalCount > 0 ? (
                <Doughnut
                    datasets={datasets}
                    hasData={hasData}
                    optionsProps={{
                        cutout: '75%',
                        plugins: {
                            legend: {
                                display: false,
                            },
                            tooltip: {
                                xAlign: 'center',
                                bodyFont: {
                                    size: 10,
                                },
                            },
                        },
                    }}
                    overall={{
                        title: t('ProfilesPage.profiles'),
                        data: totalCount?.toLocaleString('en-US'),
                    }}
                    textCenterProps={{
                        fontTitle: 'bold 14px  Rubik, sans-serif',
                        yDataOffset: 15,
                        ...getTextProps(),
                    }}
                />
            ) : null}
        </div>
    );
});

StatusChart.displayName = 'StatusChart';

export default StatusChart;
